import type { GgcBadgeProps } from '@/components/ui/ggc/badge';

/** Badge tone for an influencer's per-campaign status. Design-token based (GgcBadge),
 *  never raw hex — see `badgeStatus` below. */
export type InfluencerStatusBadge = NonNullable<GgcBadgeProps['status']>;

export interface InfluencerStatus {
  value: string;
  text: string;
  textKo: string;
  icon: string;
  /** GgcBadge tone. Single source of truth for this status's color across every page
   *  (dashboard list, influencer profile, payment review, …) so the same status never
   *  renders green on one page and blue on another. */
  badgeStatus: InfluencerStatusBadge;
  requiresToAccessAllFeatures?: boolean;
}

export const CAMPAIGN_INFLUENCER_STATUSES: InfluencerStatus[] = [
  { value: 'candidate',        text: 'Candidate',           textKo: '후보',        icon: 'child',        badgeStatus: 'muted' },
  { value: 'prospect',         text: 'Prospect',            textKo: '잠재 대상자',   icon: 'star-half-o',  badgeStatus: 'info',        requiresToAccessAllFeatures: true },
  { value: 'contacting',       text: 'Contacting',          textKo: '연락 중',     icon: 'envelope-o',   badgeStatus: 'in-progress', requiresToAccessAllFeatures: true },
  { value: 'invited',          text: 'Invited',             textKo: '초대됨',      icon: 'comments-o',   badgeStatus: 'active' },
  { value: 'negotiation',      text: 'Negotiation',         textKo: '협상',        icon: 'handshake-o',  badgeStatus: 'draft',       requiresToAccessAllFeatures: true },
  { value: 'content_creation', text: 'Content Creation',    textKo: '콘텐츠 제작', icon: 'podcast',      badgeStatus: 'in-progress' },
  { value: 'payment',          text: 'Waiting For Payment', textKo: '결제 대기',   icon: 'pause',        badgeStatus: 'warning' },
  { value: 'payment_done',     text: 'Payment Done',        textKo: '결제 완료',   icon: 'usd',          badgeStatus: 'success' },
  { value: 'completed',        text: 'Completed',           textKo: '완료',        icon: 'check',        badgeStatus: 'completed' },
  { value: 'incomplete',       text: 'Incomplete',          textKo: '미완료',      icon: 'times',        badgeStatus: 'error' },
  { value: 'rejected',         text: 'Rejected',            textKo: '거절됨',      icon: 'frown-o',      badgeStatus: 'error' },
  { value: 'deleted',          text: 'Deleted',             textKo: '삭제됨',      icon: 'trash',        badgeStatus: 'aborted',     requiresToAccessAllFeatures: true },
];

/** Legacy/aliased status values that still appear on older userCampaign records but are
 *  not part of the current status set above. Mapped so they don't fall back to gray. */
const LEGACY_STATUS_BADGE: Record<string, InfluencerStatusBadge> = {
  added: 'in-progress',
  proposed: 'draft',
  accepted: 'success',
};

const STATUS_BY_VALUE = new Map(CAMPAIGN_INFLUENCER_STATUSES.map((s) => [s.value, s]));

/** Lookup an influencer status by value (including legacy aliases). */
export function getInfluencerStatus(status?: string): InfluencerStatus | undefined {
  return STATUS_BY_VALUE.get(status ?? '');
}

/** Badge tone for any influencer status value, legacy aliases included. Unknown → 'muted'. */
export function getInfluencerStatusBadge(status?: string): InfluencerStatusBadge {
  const key = status ?? '';
  return STATUS_BY_VALUE.get(key)?.badgeStatus ?? LEGACY_STATUS_BADGE[key] ?? 'muted';
}
